import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, Loader2, ClipboardPaste, ChevronRight, Package } from "lucide-react";
import client, { money, apiErr } from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import FlowSteps from "@/components/FlowSteps";
import PageHero from "@/components/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const REFINE = {
  Apples: ["Any variety", "Gala", "Honeycrisp", "Granny Smith", "Organic only", "Bagged"],
  Milk: ["Any", "2%", "1%", "Skim", "Whole", "Organic"],
};

export default function ListBuilder() {
  const { listId } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [quick, setQuick] = useState("");
  const [bulk, setBulk] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [states, setStates] = useState({});

  const ensureList = useCallback(async () => {
    if (listId) { const { data } = await client.get(`/lists/${listId}`); return data; }
    const { data: lists } = await client.get("/lists");
    if (lists.length) { const { data } = await client.get(`/lists/${lists[0].id}`); navigate(`/list/${lists[0].id}`, { replace: true }); return data; }
    const { data: l } = await client.post("/lists", { name: "Weekly Groceries" });
    navigate(`/list/${l.id}`, { replace: true });
    return { ...l, items: [] };
  }, [listId, navigate]);

  const load = useCallback(async () => { try { setList(await ensureList()); } catch { toast.error("Could not load list"); } }, [ensureList]);
  useEffect(() => { load(); }, [load]);

  const add = async (raw) => {
    const text = (raw ?? quick).trim(); if (!text) return;
    try { await client.post(`/lists/${list.id}/items`, { raw: text }); setQuick(""); await load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const addBulk = async () => {
    if (!bulk.trim()) return;
    try { const { data } = await client.post(`/lists/${list.id}/items/bulk`, { text: bulk }); toast.success(`Added ${data.items.length} items`); setBulk(""); setBulkOpen(false); await load(); }
    catch { toast.error("Could not parse list"); }
  };
  const del = async (id) => { await client.delete(`/lists/${list.id}/items/${id}`); await load(); };
  const patch = async (id, body) => { await client.patch(`/lists/${list.id}/items/${id}`, body); await load(); };

  const checkStates = useCallback(async () => {
    if (!list?.items?.length) return;
    const entries = await Promise.all(list.items.map(async (it) => {
      try { const { data } = await client.get(`/lists/${list.id}/items/${it.id}/matches`); return [it.id, data]; }
      catch { return [it.id, null]; }
    }));
    setStates(Object.fromEntries(entries));
  }, [list]);
  useEffect(() => { checkStates(); }, [checkStates]);

  const findPlan = async () => {
    setPlanning(true);
    try { await client.post(`/lists/${list.id}/plans`); navigate(`/list/${list.id}/results`); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Add items first"); } finally { setPlanning(false); }
  };

  if (!list) return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-pp-forest" /></div>;

  const grouped = {};
  (list.items || []).forEach((it) => { (grouped[it.category] = grouped[it.category] || []).push(it); });
  const total = Object.values(states).reduce((s, d) => s + (d?.options?.[0]?.line_total || 0), 0);

  return (
    <div className="space-y-5">
    <FlowSteps current={0} listId={list.id} />
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 space-y-5">
        <PageHero image="/img/produce.jpg" eyebrow="Step 1 · Your list" title={list.name}
          subtitle={`${list.items?.length || 0} items · type naturally, like “2 litres of 2% milk”.`} testId="list-hero" />

        {/* Quick add */}
        <div className="flex gap-2">
          <Input data-testid="quick-add-input" value={quick} onChange={(e) => setQuick(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add an item… e.g. chicken breast for four people"
            className="h-12 rounded-2xl bg-white border-0 pp-shadow" />
          <Button data-testid="quick-add-button" onClick={() => add()} className="h-12 rounded-2xl bg-pp-forest px-5"><Plus className="h-5 w-5" /></Button>
          <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="bulk-add-button" className="h-12 rounded-2xl bg-white"><ClipboardPaste className="h-5 w-5" /></Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Paste multiple items</DialogTitle></DialogHeader>
              <Textarea data-testid="bulk-textarea" rows={8} value={bulk} onChange={(e) => setBulk(e.target.value)}
                placeholder={"2 litres 2% milk\none loaf whole-wheat bread\none dozen large eggs\nsix apples"} className="bg-white" />
              <Button onClick={addBulk} data-testid="bulk-submit" className="rounded-full bg-pp-forest">Add all items</Button>
            </DialogContent>
          </Dialog>
        </div>

        {/* Items grouped */}
        {list.items?.length ? (
          Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} className="bg-white rounded-3xl p-4 pp-shadow">
              <h3 className="text-xs font-bold uppercase tracking-wide text-pp-muted px-1 mb-2">{cat}</h3>
              <div className="divide-y divide-pp-border">
                {items.map((it) => {
                  const st = states[it.id];
                  return (
                    <div key={it.id} data-testid={`list-item-${it.id}`} className="py-3 flex items-center gap-3">
                      <Checkbox checked={it.checked} onCheckedChange={(v) => patch(it.id, { checked: !!v })} data-testid={`check-${it.id}`} />
                      {st?.options?.[0]?.image ? <img src={st.options[0].image} alt="" className="h-11 w-11 rounded-xl object-contain bg-white shrink-0" />
                        : <span className="h-11 w-11 rounded-xl bg-pp-cream grid place-items-center shrink-0"><Package className="h-5 w-5 text-pp-fresh" /></span>}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-semibold text-pp-text ${it.checked ? "line-through text-pp-muted" : ""}`}>{it.name}</span>
                          {it.must_buy && <span className="rounded-full bg-pp-gold/20 text-[#8A5F00] text-[10px] font-bold px-2 py-0.5">MUST BUY</span>}
                          {st && <StateBadge state={st.state} testId={`state-${it.id}`} />}
                        </div>
                        <p className="text-xs text-pp-muted mt-0.5 truncate">
                          {it.quantity} {it.unit} {it.attributes?.length ? "· " + it.attributes.join(", ") : ""}
                          {st?.options?.[0] && <> · best {money(st.options[0].line_total)} at {st.options[0].store_name}</>}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => patch(it.id, { quantity: Math.max(1, (it.quantity || 1) - 1) })} className="h-7 w-7 rounded-full bg-pp-cream text-pp-text font-bold">-</button>
                        <span className="w-6 text-center text-sm font-bold">{it.quantity}</span>
                        <button onClick={() => patch(it.id, { quantity: (it.quantity || 1) + 1 })} className="h-7 w-7 rounded-full bg-pp-cream text-pp-text font-bold">+</button>
                        <button onClick={() => del(it.id)} data-testid={`delete-${it.id}`} className="ml-1 h-7 w-7 grid place-items-center rounded-full text-pp-error hover:bg-[#FDF0ED]"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {REFINE[cat === "Produce" ? "Apples" : cat] && null}
            </div>
          ))
        ) : (
          <div className="bg-white rounded-3xl p-10 text-center pp-shadow" data-testid="empty-list">
            <Package className="h-12 w-12 mx-auto text-pp-fresh" />
            <p className="mt-3 font-bold text-pp-text">Your list is empty</p>
            <p className="text-pp-muted text-sm">Add your first item above to get started.</p>
          </div>
        )}
      </div>

      {/* Sidebar summary */}
      <div className="lg:col-span-4">
        <div className="lg:sticky lg:top-24 bg-white rounded-3xl p-5 pp-shadow space-y-4">
          <h3 className="font-extrabold text-pp-text text-lg">Ready to save?</h3>
          <div className="rounded-2xl bg-pp-cream p-4">
            <p className="text-sm text-pp-muted">Estimated lowest basket</p>
            <p className="text-3xl font-extrabold text-pp-forest">{money(total)}</p>
            <p className="text-xs text-pp-muted mt-1">Cheapest price per item across stores near you, refreshed every few hours</p>
          </div>
          <Button data-testid="find-best-plan" onClick={findPlan} disabled={planning || !list.items?.length}
            className="w-full h-12 rounded-full bg-pp-forest text-base font-bold">
            {planning ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Sparkles className="h-5 w-5 mr-2" /> Find My Best Plan</>}
          </Button>
          <p className="text-xs text-pp-muted text-center">We'll compare single-store vs. multi-store trips and factor in fuel & time.</p>
        </div>
      </div>
    </div>
    </div>
  );
}
